/* ===================================================================
   Customers - FAITHFUL port of the Claude Design mockup CUSTOMERS screen
   ("Rhody Reviews.dc.html", desktop lines 955-1026 + mobile 2277-2308).
   Corbato, 2026-07-03.

   PRESENTATION reskin only -- every live hook and product rule from the
   prior Customers is preserved:
     - useCustomers()            -> the live customer table + statline
       (Total / Left a review / Review rate derived from the review-join)
     - useCustomerMutations()    -> CSV import (parseCustomersCsv) + add +
       do-not-contact
     - useCustomerActivity(id)   -> the profile modal timeline (opens on the
       row "Profile" click)
   HARD PRODUCT RULE intact: the profile modal's "Send review request" is an
   explicit operator click; nothing here auto-sends.

   The visual is lifted VERBATIM from the prototype into cust- prefixed
   classes (styles/customers.css) -- same discipline as the faithful
   Dashboard. ONE responsive component: desktop grid table >=1024px, a
   2-up enhanced-mobile card grid 768-1023px, a stacked card list <768px.
   =================================================================== */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Icon, Avatar, Chip } from '../components/ui';
import {
  useCustomers,
  useCustomerActivity,
  useSessionToken,
  useCustomerMutations,
} from '../hooks';
import { AddCustomerModal } from '../components/AddCustomerModal';
import { parseCustomersCsv } from '../lib/csv';
import type { ActivityEvent } from '../lib/api';
import {
  fullName,
  contactLine,
  avatarColor,
  initialOf,
  customerStatus,
  relativeTime,
} from '../lib/display';
import { ClosePath } from '../components/ui';
import '../styles/components.css';
import '../styles/customers.css';

/* mockup inline SVGs, ported 1:1 --------------------------------------- */
const IconSearch = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4-4" />
  </svg>
);
const IconImport = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  </svg>
);
const IconPlus = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);
const IconCaret = (
  <svg viewBox="0 0 24 24" fill="none" stroke="#9aa0a6" strokeWidth={2}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

/* status badge kind -> the cust-badge modifier class */
const BADGE_CLASS: Record<string, string> = {
  green: 'cust-badge--green',
  gray: 'cust-badge--gray',
  gold: 'cust-badge--gold',
  red: 'cust-badge--red',
  blue: 'cust-badge--gray',
};

/* Render one live activity event into the design's title/desc timeline row.
   Each event type surfaces its own fields; nothing is invented when a field
   is missing (we just omit it). `at` is shown as a friendly relative time. */
function activityRow(ev: ActivityEvent): { title: string; desc: string } {
  const when = relativeTime(ev.at);
  if (ev.type === 'review') {
    const stars = ev.rating != null ? `${ev.rating}-star ` : '';
    const platform = ev.platform ? ` ${ev.platform}` : '';
    const text = ev.review_text ? ` - "${ev.review_text}"` : '';
    const replied = ev.replied ? ' (replied)' : '';
    return {
      title: `Left a ${stars}${platform} review${replied}`.replace(/\s+/g, ' ').trim(),
      desc: `${when}${text}`.trim() || when,
    };
  }
  if (ev.type === 'request') {
    const channel = ev.channel ? ` (${ev.channel})` : '';
    const status = ev.status ? ` - ${ev.status}` : '';
    // Truthful verb: only say "sent" when the status proves it left; a queued /
    // dry-run request reads "queued", not "sent".
    const st = (ev.status || '').toLowerCase();
    const verb = /sent|deliver|open|left_review|reminder/.test(st) ? 'sent' : 'queued';
    return {
      title: `Review request ${verb}${channel}`,
      desc: `${when}${status}`.trim() || when,
    };
  }
  // transaction
  const label = (ev.event_type || 'Activity').replace(/_/g, ' ');
  const notes = ev.notes ? ` - ${ev.notes}` : '';
  return {
    title: label.charAt(0).toUpperCase() + label.slice(1),
    desc: `${when}${notes}`.trim() || when,
  };
}

function CustomerProfileModal({
  customerId,
  fallbackName,
  fallbackColor,
  initialDoNotContact,
  onClose,
  onSend,
  onToggleDnc,
  dncPending,
}: {
  customerId: string;
  fallbackName: string;
  fallbackColor: string;
  initialDoNotContact: boolean;
  onClose: () => void;
  onSend: () => void;
  onToggleDnc: (next: boolean) => void;
  dncPending: boolean;
}) {
  // LIVE: fetch this customer's activity when the modal is open.
  const { data, isLoading, isError, error } = useCustomerActivity(customerId);
  const c = data?.customer;
  const name = c ? [c.first_name, c.last_name].filter(Boolean).join(' ').trim() || fallbackName : fallbackName;
  const color = avatarColor(name || customerId) || fallbackColor;
  const sub = c ? [c.phone, c.email].filter(Boolean).join(' - ') : '';
  const counts = data?.counts;
  const events = useMemo(() => data?.events ?? [], [data]);
  // Prefer the freshly fetched flag; fall back to the row's value while loading.
  const doNotContact = c?.do_not_contact ?? initialDoNotContact;

  return (
    <div
      className="modal-bg"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal">
        <div className="modal__head">
          <h3>Customer profile</h3>
          <button className="x" onClick={onClose} aria-label="Close">
            <Icon d={ClosePath} />
          </button>
        </div>

        <div className="modal__body">
          <div className="phead">
            <Avatar initial={initialOf(name)} color={color} />
            <div>
              <h2>{name}</h2>
              {sub && <div className="sub">{sub}</div>}
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              {doNotContact && <Chip kind="red">Do not contact</Chip>}
              {counts && counts.reviews > 0 && (
                <Chip kind="green">
                  {counts.reviews} review{counts.reviews === 1 ? '' : 's'}
                </Chip>
              )}
            </div>
          </div>

          <div className="pstats">
            <div className="pstat">
              <b>{counts ? counts.transactions : '-'}</b>
              <span>Jobs</span>
            </div>
            <div className="pstat">
              <b>{counts ? counts.reviews : '-'}</b>
              <span>Reviews left</span>
            </div>
            <div className="pstat">
              <b>{counts ? counts.requests : '-'}</b>
              <span>Requests sent</span>
            </div>
          </div>

          <div className="sectlabel">Activity</div>
          <div className="timeline">
            {isLoading && (
              <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
                Loading activity...
              </div>
            )}
            {isError && !isLoading && (
              <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
                Could not load activity{error?.message ? ` (${error.message})` : ''}.
              </div>
            )}
            {!isLoading && !isError && events.length === 0 && (
              <div className="muted" style={{ fontSize: 13, padding: '8px 0' }}>
                No activity yet.
              </div>
            )}
            {!isLoading &&
              !isError &&
              events.map((ev) => {
                const row = activityRow(ev);
                return (
                  <div className="tlitem" key={`${ev.type}-${ev.id}`}>
                    <div className="tt">{row.title}</div>
                    <div className="td">{row.desc}</div>
                  </div>
                );
              })}
          </div>
        </div>

        <div className="modal__foot">
          {/* Do-not-contact: permanent suppression. Setting it ON removes the
              customer from ALL future review requests; OFF restores them. */}
          <button
            className="btn btn--ghost"
            onClick={() => onToggleDnc(!doNotContact)}
            disabled={dncPending}
            style={doNotContact ? undefined : { color: 'var(--red)' }}
          >
            {dncPending
              ? 'Saving...'
              : doNotContact
                ? 'Allow contact'
                : 'Do not contact'}
          </button>
          <div className="grow" style={{ flex: 1 }} />
          <button className="btn btn--ghost" onClick={onClose}>
            Close
          </button>
          {/* Explicit click only - never auto-sends. Suppressed customers can't
              be asked. */}
          <button
            className="btn btn--primary"
            onClick={onSend}
            disabled={doNotContact}
            title={doNotContact ? 'This customer is marked do-not-contact.' : undefined}
          >
            Send review request
          </button>
        </div>
      </div>
    </div>
  );
}

export function Customers() {
  // The customer whose profile modal is open (null = closed). Drives the
  // per-customer activity fetch -- the hook is idle until an id is set.
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  // Add-customer modal open state.
  const [addOpen, setAddOpen] = useState(false);
  // CSV import feedback line (success/skip summary or error).
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const navigate = useNavigate();

  // Search + status filter (client-side over already-loaded rows).
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('q') ?? '');
  const [statusFilter, setStatusFilter] = useState('All customers');

  // Reflect a global-search ?q= jump into the local search box.
  useEffect(() => {
    const q = searchParams.get('q');
    if (q != null) setSearch(q);
  }, [searchParams]);

  // Bridge the Supabase login to a data-plane rr_ token (deduped globally).
  useSessionToken();

  // ---- LIVE customer data ----
  const { data, isLoading, isError, error } = useCustomers({ limit: 500 });
  const allCustomers = useMemo(() => data?.customers ?? [], [data]);
  const { importRows, setDoNotContact, isPending: mutPending } = useCustomerMutations();

  // Apply the search term + status filter client-side. Both operate over the
  // already-loaded rows -- no extra round-trip.
  const customers = useMemo(() => {
    const q = search.trim().toLowerCase();
    return allCustomers.filter((c) => {
      if (q) {
        const hay = `${fullName(c)} ${c.email ?? ''} ${c.phone ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (statusFilter !== 'All customers') {
        if (customerStatus(c).label !== statusFilter) return false;
      }
      return true;
    });
  }, [allCustomers, search, statusFilter]);

  // statline counts derived from the live payload. "Left a review" +
  // review-rate now come from the real review-join (reviewed_count /
  // review_rate), not a fabricated number.
  const total = data?.count ?? allCustomers.length;
  const reviewedCount = data?.reviewed_count ?? 0;
  const reviewRatePct =
    data?.review_rate != null ? Math.round(data.review_rate * 100) : null;

  const filtering = search.trim() !== '' || statusFilter !== 'All customers';

  // ---- action handlers (LIVE) ----
  function handleImportCsv() {
    // Open the OS file picker; parsing + upload happens in onFileChosen.
    setImportMsg(null);
    fileInputRef.current?.click();
  }

  async function onFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset the input so choosing the same file again re-fires change.
    e.target.value = '';
    if (!file) return;
    setImportMsg('Reading file...');
    try {
      const text = await file.text();
      const parsed = parseCustomersCsv(text);
      if (parsed.rows.length === 0) {
        setImportMsg(
          'No importable rows found. The CSV needs a name plus an email or phone column.',
        );
        return;
      }
      const res = await importRows(parsed.rows);
      const dropped = parsed.totalRows - parsed.rows.length;
      const skipped = res.skipped_count + dropped;
      setImportMsg(
        `Imported ${res.imported} customer${res.imported === 1 ? '' : 's'}` +
          (skipped > 0
            ? `. Skipped ${skipped} row${skipped === 1 ? '' : 's'} (missing email/phone or invalid).`
            : '.'),
      );
    } catch (err) {
      setImportMsg(
        `Import failed${err instanceof Error ? ` (${err.message})` : ''}.`,
      );
    }
  }

  function handleAddCustomer() {
    setAddOpen(true);
  }

  function handleSendRequestFromProfile() {
    // Explicit click only - never auto. Navigate to the single Requests page,
    // deep-linking this customer as the preselected recipient; close the
    // profile modal. (The old popup composer is retired -- one request screen.)
    const id = activeCustomerId;
    setActiveCustomerId(null);
    if (id) navigate(`/requests?customer=${encodeURIComponent(id)}`);
  }

  async function handleToggleDnc(next: boolean) {
    if (!activeCustomerId) return;
    try {
      await setDoNotContact(activeCustomerId, next);
      // The list invalidation (in the hook) refreshes both the table badge and
      // the profile's flag on the next fetch.
    } catch {
      /* surfaced via mutation error; the optimistic UI stays on prior value */
    }
  }

  function handleSearchChange(value: string) {
    setSearch(value);
    // Keep the URL ?q= in sync so it survives a refresh / share.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value.trim()) next.set('q', value);
        else next.delete('q');
        return next;
      },
      { replace: true },
    );
  }

  const activeCustomer = useMemo(
    () => allCustomers.find((c) => c.id === activeCustomerId) ?? null,
    [allCustomers, activeCustomerId],
  );

  const totalStr = isLoading ? '...' : total.toLocaleString();
  const reviewedStr = isLoading ? '...' : reviewedCount.toLocaleString();
  const rateStr = isLoading || reviewRatePct == null ? '...' : String(reviewRatePct);

  /* ---- one customer badge (green/gray/gold/red) ---- */
  function StatusBadge({ c }: { c: (typeof customers)[number] }) {
    const st = c.do_not_contact
      ? { label: 'Do not contact', kind: 'red' as const }
      : customerStatus(c);
    return (
      <span className={`cust-badge ${BADGE_CLASS[st.kind] ?? 'cust-badge--gray'}`}>
        <span className="cust-badge__dot" />
        {st.label}
      </span>
    );
  }

  return (
    <div className="cust">
      {/* DESKTOP header actions. The shell topbar already carries the
          "Customers" title + subtitle + global search (see AppShell), so we do
          NOT repeat those here (that would double the prototype's header) -- we
          surface the prototype header's Import CSV / Add customer buttons,
          which the shell topbar lacks. Faithful, non-duplicative. */}
      <div className="cust-head cust-desktop-tools">
        <div />
        <div className="cust-head__actions">
          <button className="cust-btn cust-btn--ghost" onClick={handleImportCsv}>
            {IconImport}
            Import CSV
          </button>
          <button className="cust-btn cust-btn--primary" onClick={handleAddCustomer}>
            {IconPlus}
            Add customer
          </button>
        </div>
      </div>

      <div className="cust-body">
        {/* Hidden file input the Import buttons trigger (shared desktop + mobile). */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: 'none' }}
          onChange={onFileChosen}
        />

        {/* ---- DESKTOP stats row (3-up) ---- */}
        <div className="cust-stats">
          <div
            className={'cust-tile' + (statusFilter === 'All customers' ? ' is-active' : '')}
            onClick={() => setStatusFilter('All customers')}
          >
            <div className="cust-tile__k">TOTAL CUSTOMERS</div>
            <div className="cust-tile__v">{totalStr}</div>
            <div className="cust-tile__d">In your list</div>
          </div>
          <div
            className={'cust-tile' + (statusFilter === 'Reviewed' ? ' is-active' : '')}
            onClick={() => setStatusFilter('Reviewed')}
          >
            <div className="cust-tile__k">LEFT A REVIEW</div>
            <div className="cust-tile__v cust-tile__v--green">{reviewedStr}</div>
            <div className="cust-tile__d">Of {totalStr} contacts</div>
          </div>
          <div
            className={'cust-tile' + (statusFilter === 'Reviewed' ? ' is-active' : '')}
            onClick={() => setStatusFilter('Reviewed')}
          >
            <div className="cust-tile__k">REVIEW RATE</div>
            <div className="cust-tile__v">
              {rateStr}
              <span className="pct">%</span>
            </div>
            <div className="cust-tile__d">Contacts who reviewed</div>
          </div>
        </div>

        {/* ---- MOBILE stats tiles (3-up compact) ---- */}
        <div className="cust-mstats">
          <div
            className={'cust-mtile' + (statusFilter === 'All customers' ? ' is-active' : '')}
            onClick={() => setStatusFilter('All customers')}
          >
            <div className="cust-mtile__k">TOTAL</div>
            <div className="cust-mtile__v">{totalStr}</div>
          </div>
          <div
            className={'cust-mtile' + (statusFilter === 'Reviewed' ? ' is-active' : '')}
            onClick={() => setStatusFilter('Reviewed')}
          >
            <div className="cust-mtile__k">REVIEWED</div>
            <div className="cust-mtile__v cust-mtile__v--green">{reviewedStr}</div>
          </div>
          <div
            className={'cust-mtile' + (statusFilter === 'Reviewed' ? ' is-active' : '')}
            onClick={() => setStatusFilter('Reviewed')}
          >
            <div className="cust-mtile__k">RATE</div>
            <div className="cust-mtile__v">{rateStr}%</div>
          </div>
        </div>

        {/* ---- MOBILE primary action row (Add / Import, 52px tap targets) ---- */}
        <div className="cust-mactions">
          <button className="cust-mbtn cust-mbtn--primary" onClick={handleAddCustomer}>
            {IconPlus}
            Add
          </button>
          <button className="cust-mbtn cust-mbtn--ghost" onClick={handleImportCsv}>
            {IconImport}
            Import
          </button>
        </div>

        {/* ---- DESKTOP search + filter toolbar ---- */}
        <div className="cust-tools cust-desktop-tools">
          <div className="cust-search">
            {IconSearch}
            <input
              placeholder="Search customers…"
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
            />
          </div>
          <div className="cust-filter">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option>All customers</option>
              <option>Reviewed</option>
              <option>Not yet asked</option>
              <option>Win-back</option>
            </select>
            <span className="cust-filter__caret">{IconCaret}</span>
          </div>
        </div>

        {/* ---- MOBILE search (pill, full width) ---- */}
        <div className="cust-search cust-msearch">
          {IconSearch}
          <input
            placeholder="Search customers…"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
        </div>

        {/* CSV import feedback line. */}
        {importMsg && <div className="cust-importmsg">{importMsg}</div>}

        {/* ============ DESKTOP grid table (>=1024px) ============ */}
        <div className="cust-table">
          <div className="cust-thead">
            <div>CUSTOMER</div>
            <div>CONTACT</div>
            <div>TYPE</div>
            <div>STATUS</div>
            <div />
          </div>

          {isLoading && <div className="cust-state">Loading customers...</div>}
          {isError && !isLoading && (
            <div className="cust-state">
              Could not load customers{error?.message ? ` (${error.message})` : ''}.
            </div>
          )}
          {!isLoading && !isError && customers.length === 0 && (
            <div className="cust-state">
              <div className="cust-state__title">No customers yet</div>
              <div>Import a CSV or add your first customer to start collecting reviews.</div>
            </div>
          )}
          {!isLoading &&
            !isError &&
            customers.map((c) => {
              const name = fullName(c);
              return (
                <div className="cust-row" key={c.id}>
                  <div className="cust-who">
                    <div className="cust-avatar" style={{ background: avatarColor(name) }}>
                      {initialOf(name)}
                    </div>
                    <div className="cust-who__meta">
                      <div className="cust-who__name">{name}</div>
                      <div className="cust-who__sub">{contactLine(c)}</div>
                    </div>
                  </div>
                  <div className="cust-cell-contact">{c.phone || c.email || '-'}</div>
                  <div className="cust-cell-type">Customer</div>
                  <div>
                    <StatusBadge c={c} />
                  </div>
                  <div className="cust-cell-action">
                    <button className="cust-profile-btn" onClick={() => setActiveCustomerId(c.id)}>
                      Profile
                    </button>
                  </div>
                </div>
              );
            })}
        </div>

        {/* ============ MOBILE card list (<1024px; 2-up 768-1023) ============ */}
        <div className="cust-mlist">
          {isLoading && <div className="cust-state">Loading customers...</div>}
          {isError && !isLoading && (
            <div className="cust-state">
              Could not load customers{error?.message ? ` (${error.message})` : ''}.
            </div>
          )}
          {!isLoading && !isError && customers.length === 0 && (
            <div className="cust-state">
              <div className="cust-state__title">No customers yet</div>
              <div>Import a CSV or add your first customer to start collecting reviews.</div>
            </div>
          )}
          {!isLoading &&
            !isError &&
            customers.map((c) => {
              const name = fullName(c);
              return (
                <div className="cust-mcard" key={c.id} onClick={() => setActiveCustomerId(c.id)}>
                  <div className="cust-mcard__avatar" style={{ background: avatarColor(name) }}>
                    {initialOf(name)}
                  </div>
                  <div className="cust-mcard__body">
                    <div className="cust-mcard__name">{name}</div>
                    <div className="cust-mcard__sub">{c.phone || c.email || '-'}</div>
                  </div>
                  <StatusBadge c={c} />
                </div>
              );
            })}
        </div>

        {/* small filtered-count line so the "Showing" count stays visible
            (matches the prior screen's statline "Showing" affordance). */}
        {filtering && !isLoading && !isError && (
          <div className="cust-importmsg" style={{ background: 'transparent', border: 'none', padding: '0 2px' }}>
            Showing {customers.length.toLocaleString()} of {total.toLocaleString()}
          </div>
        )}
      </div>

      {activeCustomerId && (
        <CustomerProfileModal
          customerId={activeCustomerId}
          fallbackName={activeCustomer ? fullName(activeCustomer) : 'Customer'}
          fallbackColor={avatarColor(activeCustomer ? fullName(activeCustomer) : activeCustomerId)}
          initialDoNotContact={Boolean(activeCustomer?.do_not_contact)}
          onClose={() => setActiveCustomerId(null)}
          onSend={handleSendRequestFromProfile}
          onToggleDnc={handleToggleDnc}
          dncPending={mutPending}
        />
      )}

      {addOpen && <AddCustomerModal onClose={() => setAddOpen(false)} />}
    </div>
  );
}
